import { Container, Sprite, Text } from 'pixi.js';
import { ResizeData } from '../../helpers/ResizeData';
import { BASE_FONT, MainSignals, TickCallData, EVENT } from '../../settings/constants';
import { HorizontalAlign, TextSettings, VerticalAlign } from '../../settings/texts';
import { TweenMax } from 'gsap';
import { playSpeech, stopSpeech } from '../../helpers/SoundManager';
import { Howl } from 'howler';

const MARGIN = 70;
const BG_PADDING_W = 28;
const BG_PADDING_H = 22;
const SCROLLBAR_WIDTH = 20;

export default class SceneBase extends Container {
  signals: MainSignals;
  settings: TextSettings;

  isVisible = false;

  bg: Sprite;
  text?: Text;

  show = 0;
  hide = 0;

  speech?: Howl;
  soundDelay: any = null;

  constructor(signals: MainSignals, settings: TextSettings) {
    super();

    // vars
    this.signals = signals;
    this.settings = settings;
    this.parseSettings();

    // // props
    this.visible = false;
    this.alpha = 0;

    // parts
    this.bg = this.setBg();

    // listeners
    signals.resizeCall.add(this.resizeCall, this);
    signals.tickCall.add(this.tickCall, this);
  }

  // --------------------------------------------------------------
  // Settings
  // --------------------------------------------------------------

  parseSettings(): void {
    this.show = this.settings.show;
    this.hide = this.settings.hide;
  }

  // --------------------------------------------------------------
  // Bg
  // --------------------------------------------------------------

  setBg(): Sprite {
    const bg = Sprite.from(this.settings.letter ? 'text-bg-light.png' : 'text-bg-dark.png');
    bg.x = -BG_PADDING_W;
    bg.y = -BG_PADDING_H;
    bg.alpha = 0.8;
    this.addChild(bg);
    return bg;
  }

  updateBg(scale: number): void {
    if (this.text) {
      this.bg.x = -BG_PADDING_W * scale;
      this.bg.y = -BG_PADDING_H * scale;
      this.bg.width = this.text.width + 2 * BG_PADDING_W * scale;
      this.bg.height = this.text.height + 2 * BG_PADDING_H * scale;
    }
  }

  // --------------------------------------------------------------
  // Text
  // --------------------------------------------------------------

  setText(data: ResizeData): void {
    // try to destroy old
    if (this.text) {
      this.removeChild(this.text);
      this.text.destroy();
    }
    // set new
    this.text = new Text(this.settings.text, {
      fontFamily: BASE_FONT,
      fontStyle: this.settings.letter ? 'italic' : 'normal',
      fontSize: Math.max(12, Math.round(18 * data.stage.scale)),
      fill: 0x1a1b25,
      wordWrap: true,
      wordWrapWidth: data.window.width * 0.6,
    });
    this.addChild(this.text);
  }

  // --------------------------------------------------------------
  // Resize
  // --------------------------------------------------------------

  resizeCall(data: ResizeData): void {
    const { scale } = data.stage;

    // parts
    this.setText(data);
    this.updateBg(scale);

    // vars
    const margin = MARGIN * scale;
    const txWidth = this.text?.width ?? 0;
    const txHeight = this.text?.height ?? 0;

    // x position
    switch (this.settings.horizontal) {
      case HorizontalAlign.Left:
        this.x = margin;
        break;
      case HorizontalAlign.Center:
        this.x = data.window.width * 0.5 - txWidth * 0.5;
        break;
      case HorizontalAlign.Right:
        this.x = data.window.width - txWidth - margin - SCROLLBAR_WIDTH;
        break;
    }

    // y position
    switch (this.settings.vertical) {
      case VerticalAlign.Top:
        if (this.settings.horizontal === HorizontalAlign.Right) {
          this.y = margin + 35 * scale;
        } else {
          this.y = margin;
        }
        break;
      case VerticalAlign.Center:
        this.y = data.window.height * 0.5 - txHeight * 0.5;
        break;
      case VerticalAlign.Bottom:
        if (this.settings.horizontal === HorizontalAlign.Right) {
          this.y = data.window.height - txHeight - margin - 35 * scale;
        } else {
          this.y = data.window.height - txHeight - margin;
        }
        break;
    }
  }

  // --------------------------------------------------------------
  // Tick
  // --------------------------------------------------------------

  tickCall(data: TickCallData): void {
    if (data.scroll >= this.show && data.scroll <= this.hide) {
      this.showText(true);
    } else {
      this.showText(false);
    }
  }

  // --------------------------------------------------------------
  // Show
  // --------------------------------------------------------------

  showText(show: boolean): void {
    if (this.isVisible !== show) {
      this.isVisible = show;

      TweenMax.killTweensOf(this);
      if (this.isVisible) {
        this.visible = true;
        TweenMax.to(this, 0.9, { alpha: 1 });
        this.playSound();
      } else {
        TweenMax.to(this, 0.6, { alpha: 0, onComplete: this.hideComplete.bind(this) });
        this.stopSound();
      }
    }
  }

  hideComplete(): void {
    this.visible = false;
  }

  // --------------------------------------------------------------
  // Sound
  // --------------------------------------------------------------

  playSound(): void {
    this.soundDelay = setTimeout(() => {
      this.startSound();
    }, 200);
  }

  startSound(): void {
    playSpeech(this.settings.speech, this.soundComplete.bind(this));
  }

  soundComplete(): void {
    this.signals.stateCall.dispatch(EVENT.STATE.SPEECH_DONE, this.settings.speech);
  }

  stopSound(): void {
    clearTimeout(this.soundDelay);
    stopSpeech();
  }
}
